/* ===== Joy Assistant — preview live pe designul Custom (pagina se construiește pas cu pas) ===== */
import { getCurrentSettings } from '../services/settings.js?v=83.21';
import { overlayFreshInMemory } from './joy-fresh.js?v=83.21';
import {
  JOY_MODULE_SUBSECTION,
  JOY_PREVIEW_CLICK_SELECTORS,
  buildJoyPreviewSettings
} from '../services/joy-assistant.js?v=83.21';
import { logger } from '../utils/logger.js?v=83.21';
import { showSnackbar } from '../utils/ui.js?v=83.21';
import {
  openClientInterfaceSubsection,
  closeClientInterfaceModulePopup
} from './client-interface-nav.js?v=83.21';
import {
  applyElegantTemplateDomFixes,
  syncElegantTopChrome,
  isCustomTemplateActive,
  CLIENT_HEADER_SOCIAL_SVGS
} from './client-template-style.js?v=83.21';
import { sanitizeImageSrc } from '../utils/url.js?v=83.21';

let settingsBackup = null;
let previewClickBound = false;
let moduleSaveListener = null;
let pendingModuleId = null;

function isJoyPreviewActive() {
  return (
    document.body.classList.contains('joy-assistant-active') &&
    document.getElementById('joyAssistantPage')?.classList.contains('visible')
  );
}

function isElementVisible(el) {
  if (!el) return false;
  const st = getComputedStyle(el);
  if (st.display === 'none' || st.visibility === 'hidden' || st.opacity === '0') return false;
  const r = el.getBoundingClientRect();
  return r.width > 2 && r.height > 2;
}

function showEl(el, display = 'inline-flex') {
  if (!el) return;
  el.style.display = display;
  el.removeAttribute('aria-hidden');
  el.classList.remove('hidden');
}

function markJoyConfig(el, moduleId) {
  if (!el || !moduleId) return;
  el.dataset.joyConfigModule = moduleId;
  el.classList.add('joy-preview-config-target');
}

function clearJoyConfigMarks() {
  document.querySelectorAll('[data-joy-config-module]').forEach((el) => {
    el.removeAttribute('data-joy-config-module');
    el.classList.remove('joy-preview-config-target');
  });
}

function patchSettingsForPreview(settings, joyState, { buildMode = false } = {}) {
  if (!settings || !joyState) return;
  if (!settingsBackup) {
    settingsBackup = {
      bgURLs: Array.isArray(settings.bgURLs) ? [...settings.bgURLs] : [],
      bgURL: settings.bgURL,
      slideDuration: settings.slideDuration
    };
  }
  const joyBgs = (joyState.images || []).filter(
    (u) => typeof u === 'string' && (u.startsWith('blob:') || u.startsWith('http'))
  );
  if (joyBgs.length) {
    settings.bgURLs = [...joyBgs];
    settings.bgURL = joyBgs[0];
  } else if (buildMode) {
    // Build mode: pagina pornește goală — nu arătăm pozele deja salvate în setări
    settings.bgURLs = [];
    settings.bgURL = '';
  }
  if (buildMode) {
    // Sursă de adevăr pentru tema Custom: sync-urile ei pot rula mai târziu
    // cu setări proaspete din Firestore (fără patch) — citește de aici.
    window.__joyPreviewImages = [...joyBgs];
  }
  settings.slideDuration = joyState.slideshowSeconds || settings.slideDuration || 5;
}

async function applyJoyBackground(joyState) {
  if (!isJoyPreviewActive() || !joyState) return;
  const joyBgs = (joyState.images || []).filter(
    (u) => typeof u === 'string' && (u.startsWith('blob:') || u.startsWith('http'))
  );
  if (!joyBgs.length) return;
  try {
    const { initSlideshow } = await import('./slideshow.js?v=83.21');
    const sec = joyState.slideshowSeconds || getCurrentSettings()?.slideDuration || 5;
    initSlideshow(joyBgs, sec);
  } catch (e) {
    logger.warn('[JoyAssistant] applyJoyBackground failed:', e);
  }
}

export function restoreSettingsAfterJoyPreview() {
  const settings = getCurrentSettings();
  if (settings && settingsBackup) {
    settings.bgURLs = settingsBackup.bgURLs;
    settings.bgURL = settingsBackup.bgURL;
    settings.slideDuration = settingsBackup.slideDuration;
  }
  settingsBackup = null;
  clearJoyConfigMarks();
}

/** Forțează fluxul flex Mod Client (stage/hero nu mai sunt fixed). */
function fixJoyClientBottomLayout() {
  const page = document.getElementById('clientPage');
  if (!page) return;

  if (page.classList.contains('template-elegant')) {
    applyElegantTemplateDomFixes();
  }

  const stage = document.getElementById('clientPremiumStage');
  const hero = document.getElementById('clientHeroActions');
  const cta = document.getElementById('clientPremiumCenterCta');
  const dock = document.getElementById('clientPremiumBottomDock');
  const duo = document.getElementById('clientPremiumSectionDuo');

  [stage, hero].forEach((el) => {
    if (!el) return;
    el.style.setProperty('display', 'contents', 'important');
    el.style.setProperty('position', 'static', 'important');
    el.style.removeProperty('inset');
    el.style.removeProperty('top');
    el.style.removeProperty('bottom');
    el.style.removeProperty('left');
    el.style.removeProperty('right');
    el.style.removeProperty('height');
    el.style.removeProperty('min-height');
    el.style.removeProperty('padding-bottom');
  });

  [cta, dock, duo].forEach((el) => {
    if (!el) return;
    el.style.setProperty('position', 'relative', 'important');
    el.style.removeProperty('top');
    el.style.removeProperty('bottom');
    el.style.removeProperty('left');
    el.style.removeProperty('right');
    el.style.removeProperty('transform');
  });
}

/** Poziționare robot + clase layout — după ce applyClientUI a arătat ce există deja. */
/** Bară fixă logo + social pe stratul Joy (vizibil deasupra preview-ului client). */
function syncJoyPreviewTopChrome(joyState, settings = {}) {
  const chrome = document.getElementById('joyPreviewTopChrome');
  const logoWrap = document.getElementById('joyPreviewTopLogo');
  const socialWrap = document.getElementById('joyPreviewTopSocial');
  if (!chrome || !logoWrap || !socialWrap) return;

  if (!isJoyPreviewActive()) {
    chrome.hidden = true;
    chrome.setAttribute('aria-hidden', 'true');
    logoWrap.replaceChildren();
    socialWrap.replaceChildren();
    return;
  }

  const step3 = new Set(joyState?.step3Modules || []);
  const logoUrl = String(settings?.locationLogoURL || '').trim();
  const showLogo = step3.has('logo');
  const socialIds = ['instagram', 'facebook', 'tiktok'].filter((id) => step3.has(id));

  if (!showLogo && !socialIds.length) {
    chrome.hidden = true;
    chrome.setAttribute('aria-hidden', 'true');
    logoWrap.replaceChildren();
    socialWrap.replaceChildren();
    return;
  }

  chrome.hidden = false;
  chrome.removeAttribute('hidden');
  chrome.removeAttribute('aria-hidden');

  logoWrap.replaceChildren();
  if (showLogo) {
    if (logoUrl) {
      const img = document.createElement('img');
      img.className = 'joy-preview-top-chrome__logo-img';
      img.src = logoUrl;
      img.alt = 'Logo';
      img.width = 56;
      img.height = 56;
      img.decoding = 'async';
      img.dataset.joyConfigModule = 'logo';
      logoWrap.appendChild(img);
    } else {
      const ph = document.createElement('button');
      ph.type = 'button';
      ph.className = 'joy-preview-top-chrome__logo-ph';
      ph.dataset.joyConfigModule = 'logo';
      ph.setAttribute('aria-label', 'Configurează logo');
      ph.textContent = '🏷️';
      logoWrap.appendChild(ph);
    }
  }

  socialWrap.replaceChildren();
  const labels = { instagram: 'Instagram', facebook: 'Facebook', tiktok: 'TikTok' };
  socialIds.forEach((id) => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = `joy-preview-top-chrome__social-btn joy-preview-top-chrome__social-btn--${id}`;
    btn.dataset.joyConfigModule = id;
    btn.setAttribute('aria-label', labels[id] || id);
    btn.innerHTML = CLIENT_HEADER_SOCIAL_SVGS[id] || '';
    socialWrap.appendChild(btn);
  });
}

function hideJoyPreviewTopChrome() {
  const chrome = document.getElementById('joyPreviewTopChrome');
  if (!chrome) return;
  chrome.hidden = true;
  chrome.setAttribute('aria-hidden', 'true');
  document.getElementById('joyPreviewTopLogo')?.replaceChildren();
  document.getElementById('joyPreviewTopSocial')?.replaceChildren();
}

function updateJoyLayoutHints() {
  const clientPage = document.getElementById('clientPage');
  const joyPage = document.getElementById('joyAssistantPage');
  const dock = document.getElementById('clientPremiumBottomDock');
  const duo = document.getElementById('clientPremiumSectionDuo');
  const cta = document.getElementById('clientPremiumCenterCta');
  const promo = document.getElementById('layoutBlockPromo');
  const social = document.getElementById('clientElegantSocialSection');

  const hasFab = isElementVisible(dock);
  const hasDuo = isElementVisible(duo);
  const hasCta = isElementVisible(cta);

  clientPage?.classList.toggle('joy-client-has-fab', hasFab);
  clientPage?.classList.toggle('joy-client-has-duo', hasDuo);
  // 'joy-preview-empty' e gestionat de applyJoyBuildMask (build mode)
  if (!clientPage?.hasAttribute('data-joy-build')) {
    clientPage?.classList.remove('joy-preview-empty');
  }

  const applyLayoutVars = () => {
    const bottomStack = [cta, duo, dock].filter(isElementVisible);
    let reserve = 24;
    if (bottomStack.length) {
      const topMost = Math.min(...bottomStack.map((el) => el.getBoundingClientRect().top));
      if (topMost > 0 && topMost < window.innerHeight) {
        reserve = Math.max(24, Math.round(window.innerHeight - topMost + 12));
      }
    }

    let topChrome = 52;
    const topCandidates = [
      social,
      promo,
      document.getElementById('clientPremiumSectionPromo'),
      document.getElementById('clientTemplateHeader'),
      document.querySelector('#clientPage .client-template-header__top')
    ];
    topCandidates.forEach((el) => {
      if (!isElementVisible(el)) return;
      const b = el.getBoundingClientRect().bottom;
      if (b > topChrome && b < window.innerHeight * 0.5) topChrome = Math.min(b + 10, 180);
    });

    joyPage?.style.setProperty(
      '--joy-client-reserve',
      `calc(${reserve}px + env(safe-area-inset-bottom, 0px))`
    );
    joyPage?.style.setProperty(
      '--joy-top-chrome',
      `calc(${topChrome}px + env(safe-area-inset-top, 0px))`
    );
  };

  applyLayoutVars();
  requestAnimationFrame(applyLayoutVars);
}

/** Module bifate în draft dar încă neconfigurate — le arătăm în preview. */
function applyJoyDraftExtras(joyState) {
  const ops = new Set(joyState.step2Modules || []);
  const fabMap = {
    wifi: 'clientWifiFab',
    location: 'clientLocationMapFab',
    hours: 'clientLocationHoursFab',
    reservation: 'clientReservationFab'
  };

  if (ops.size) {
    const dock = document.getElementById('clientPremiumBottomDock');
    const fabBar = document.getElementById('clientFabBar');
    if (dock) showEl(dock, 'flex');
    if (fabBar) showEl(fabBar, 'flex');
    Object.entries(fabMap).forEach(([id, elId]) => {
      if (!ops.has(id)) return;
      const fab = document.getElementById(elId);
      showEl(fab, 'inline-flex');
      if (fab) {
        fab.style.order = id === 'wifi' ? '1' : id === 'hours' ? '2' : id === 'location' ? '3' : '4';
      }
    });
  }

  const picked = new Set(joyState.step3Modules || []);
  if (picked.has('menu')) {
    showEl(document.getElementById('layoutBlockMenu'), 'flex');
    showEl(document.getElementById('locationMenuBtn'), 'flex');
  }
  if (picked.has('googleReview')) {
    showEl(document.getElementById('layoutBlockReview'), 'flex');
    showEl(document.getElementById('feedbackBtn'), 'flex');
  }
  const socialPicked = ['facebook', 'instagram', 'tiktok'].some((id) => picked.has(id));
  if (socialPicked || picked.has('logo')) {
    const elegant = document.getElementById('clientElegantSocialSection');
    if (elegant) {
      elegant.hidden = true;
      elegant.style.display = 'none';
      elegant.setAttribute('aria-hidden', 'true');
    }
  }
  if (picked.size) {
    showEl(document.getElementById('clientHeroActions'), 'flex');
    showEl(document.getElementById('clientPremiumSectionDuo'), 'flex');
  }

  const sec = joyState.editableSection || {};
  const title = String(sec.title || '').trim();
  const text = String(sec.text || '').trim();
  const imageUrl = String(sec.imageUrl || '').trim();
  if (title || text || imageUrl) {
    const wrap = document.getElementById('layoutBlockPromo');
    const trigger = document.getElementById('clientPromoGalleryTrigger');
    const label = document.getElementById('clientPromoGalleryTriggerLabel');
    const hero = document.getElementById('clientHeroActions');
    if (wrap) {
      wrap.style.display = 'flex';
      wrap.removeAttribute('aria-hidden');
    }
    if (hero) showEl(hero, 'flex');
    if (trigger) showEl(trigger, 'flex');
    if (label) label.textContent = title || 'Ofertele noastre';
  }
}

/* ===== Build mode (design Custom) — pagina pornește goală și se construiește din draft ===== */

function revealEl(el) {
  if (!el || el.dataset.joyRevealed === '1') return;
  el.dataset.joyRevealed = '1';
  el.classList.add('joy-appear');
  el.addEventListener('animationend', () => el.classList.remove('joy-appear'), { once: true });
}

/*
 * Vizibilitatea e dublată de atributul data-joy-mask + reguli CSS (joy-assistant.css):
 * sync-urile temei pot curăța stilurile inline, dar atributul rămâne.
 */
function joyHide(el) {
  if (!el) return;
  el.dataset.joyMask = 'hide';
  el.style.setProperty('display', 'none', 'important');
  el.setAttribute('aria-hidden', 'true');
  delete el.dataset.joyRevealed;
}

function joyShow(el, display = '') {
  if (!el) return;
  el.dataset.joyMask = 'show';
  el.hidden = false;
  el.removeAttribute('hidden');
  el.removeAttribute('aria-hidden');
  if (display) el.style.setProperty('display', display, 'important');
  else if (el.style.display === 'none') el.style.removeProperty('display');
  revealEl(el);
}

const JOY_GROWTH_PLACEHOLDERS = {
  campaign: { kind: 'campaign', badge: 'CAMPANIE ACTIVĂ', icon: '🎯', title: 'Campania ta', desc: 'O configurezi din admin după salvare.' },
  loyalty: { kind: 'loyalty', badge: 'FIDELIZARE ACTIVĂ', icon: '⭐', title: 'Program de fidelizare', desc: 'Îl configurezi din admin după salvare.' },
  events: { kind: 'event', badge: 'EVENIMENT ACTIV', icon: '📅', title: 'Evenimentele tale', desc: 'Le configurezi din admin după salvare.' }
};

function populateJoyGrowthCard(card, growthId) {
  const ph = JOY_GROWTH_PLACEHOLDERS[growthId];
  if (!card || !ph) return;
  card.dataset.connectModule = ph.kind;
  let inner = card.querySelector('.client-custom-module-card');
  if (!inner) {
    inner = document.createElement('span');
    inner.className = 'client-custom-module-card';
    inner.innerHTML = `
      <span class="client-custom-module-card__icon" aria-hidden="true"></span>
      <span class="client-custom-module-card__body">
        <span class="client-custom-module-card__badge"></span>
        <span class="client-custom-module-card__title"></span>
        <span class="client-custom-module-card__desc"></span>
      </span>
      <span class="client-custom-module-card__arrow" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9 6l6 6-6 6"/></svg>
      </span>
    `;
    card.prepend(inner);
  }
  const icon = inner.querySelector('.client-custom-module-card__icon');
  const badge = inner.querySelector('.client-custom-module-card__badge');
  const title = inner.querySelector('.client-custom-module-card__title');
  const desc = inner.querySelector('.client-custom-module-card__desc');
  if (icon && !icon.textContent) icon.textContent = ph.icon;
  if (badge) {
    badge.textContent = ph.badge;
    badge.dataset.moduleKind = ph.kind;
  }
  if (title && !String(title.textContent || '').trim()) {
    title.textContent = ph.title;
    title.hidden = false;
    title.removeAttribute('hidden');
  }
  if (desc && !String(desc.textContent || '').trim()) {
    desc.textContent = ph.desc;
    desc.hidden = false;
    desc.removeAttribute('hidden');
  }
}

function syncJoyHeaderForBuild(joyState, settings = {}) {
  const picked = new Set(joyState.step3Modules || []);
  const socialRow = document.getElementById('clientTemplateSocialRow');

  if (socialRow) {
    const platforms = ['instagram', 'facebook', 'tiktok'];
    platforms.forEach((id) => {
      let btn = socialRow.querySelector(`.client-template-header__social-btn--${id}`);
      if (picked.has(id)) {
        if (!btn) {
          btn = document.createElement('button');
          btn.type = 'button';
          btn.className = `client-template-header__social-btn client-template-header__social-btn--${id} client-custom-social-btn`;
          btn.setAttribute('aria-label', id);
          btn.innerHTML = CLIENT_HEADER_SOCIAL_SVGS[id] || '';
          socialRow.appendChild(btn);
        }
        btn.dataset.joyConfigModule = id;
        btn.classList.add('joy-preview-config-target');
        joyShow(btn, 'inline-flex');
      } else if (btn) {
        joyHide(btn);
      }
    });
  }

  // Logo — designul Custom nu are logo propriu; în build îl arătăm lângă numele firmei
  const brandWrap = document.querySelector('#clientTemplateHeader .client-template-header__brand-wrap');
  let logoChip = document.getElementById('joyCustomHeaderLogo');
  if (picked.has('logo') && brandWrap) {
    const logoUrl = sanitizeImageSrc(String(settings?.locationLogoURL || '').trim());
    if (!logoChip) {
      logoChip = document.createElement('span');
      logoChip.id = 'joyCustomHeaderLogo';
      logoChip.className = 'joy-custom-header-logo';
      brandWrap.prepend(logoChip);
    }
    logoChip.innerHTML = logoUrl
      ? `<img src="${logoUrl}" alt="Logo" decoding="async" />`
      : '<span class="joy-custom-header-logo__ph" aria-hidden="true">🏷️</span>';
    logoChip.dataset.joyConfigModule = 'logo';
    logoChip.classList.add('joy-preview-config-target');
    joyShow(logoChip, 'inline-flex');
  } else if (logoChip) {
    joyHide(logoChip);
  }
}

/** Vizibilitate condusă de draft: doar ce a bifat userul apare pe pagină. */
function applyJoyBuildMask(joyState, settings = {}) {
  const page = document.getElementById('clientPage');
  if (!page || !isCustomTemplateActive(page)) return;

  page.setAttribute('data-joy-build', '1');

  const images = (joyState.images || []).filter(
    (u) => typeof u === 'string' && (u.startsWith('blob:') || u.startsWith('http'))
  );
  page.classList.toggle('joy-preview-empty', images.length === 0);

  // Utilități (dock jos)
  const utils = new Set(joyState.step2Modules || []);
  const fabMap = {
    wifi: 'clientWifiFab',
    location: 'clientLocationMapFab',
    hours: 'clientLocationHoursFab',
    reservation: 'clientReservationFab'
  };
  Object.entries(fabMap).forEach(([id, elId]) => {
    const fab = document.getElementById(elId);
    if (utils.has(id)) joyShow(fab, 'inline-flex');
    else joyHide(fab);
  });
  const dock = document.getElementById('clientPremiumBottomDock');
  const fabBar = document.getElementById('clientFabBar');
  if (utils.size) {
    joyShow(dock);
    joyShow(fabBar, 'flex');
  } else {
    joyHide(dock);
  }

  // Meniu & Recenzie (duo)
  const picked = new Set(joyState.step3Modules || []);
  const duoSection = document.getElementById('clientPremiumSectionDuo');
  const menuBlock = document.getElementById('layoutBlockMenu');
  const reviewBlock = document.getElementById('layoutBlockReview');
  if (picked.has('menu')) {
    joyShow(menuBlock);
    joyShow(document.getElementById('locationMenuBtn'), 'flex');
  } else {
    joyHide(menuBlock);
  }
  if (picked.has('googleReview')) {
    joyShow(reviewBlock);
    joyShow(document.getElementById('feedbackBtn'), 'flex');
  } else {
    joyHide(reviewBlock);
  }
  if (picked.has('menu') || picked.has('googleReview')) {
    joyShow(duoSection);
  } else {
    joyHide(duoSection);
    joyHide(document.getElementById('clientCustomNeedsTitle'));
  }

  // Header: ascuns pe pagina goală (brandul estompat ține locul); apoi brand + social/logo bifate
  const header = document.getElementById('clientTemplateHeader');
  if (images.length === 0) {
    joyHide(header);
  } else {
    // Chrome-ul Custom poate fi scos de un ciclu restore/apply al temei — îl reafirmăm
    header?.classList.add('client-custom-top-chrome');
    const brandEl = document.getElementById('clientTemplateBrand');
    if (brandEl) {
      const company = String(settings?.companyName || '').trim();
      if (company && !String(brandEl.textContent || '').trim()) brandEl.textContent = company;
      brandEl.hidden = !brandEl.textContent;
      if (brandEl.textContent) brandEl.removeAttribute('hidden');
    }
    joyShow(header, 'block');
  }
  syncJoyHeaderForBuild(joyState, settings);

  // Oferta (card în rândul promo) + modul de creștere
  const sec = joyState.editableSection || {};
  const hasOffer = !!(
    String(sec.title || '').trim() ||
    String(sec.text || '').trim() ||
    String(sec.imageUrl || '').trim()
  );
  const growthId = ['campaign', 'loyalty', 'events'].includes(joyState.growthModule)
    ? joyState.growthModule
    : null;

  const promoRow = document.getElementById('clientConnectPromoRow');
  const offerCard = document.getElementById('clientConnectOfferCard');
  const campaignCard = document.getElementById('clientConnectCampaignCard');

  if (hasOffer && offerCard) {
    const titleEl = document.getElementById('clientConnectOfferTitle');
    if (titleEl) titleEl.textContent = String(sec.title || '').trim() || 'Ofertele noastre';
    const media = document.getElementById('clientConnectOfferMedia');
    const imgUrl = sanitizeImageSrc(String(sec.imageUrl || '').trim());
    if (media) {
      media.innerHTML = imgUrl ? `<img src="${imgUrl}" alt="" decoding="async" />` : '';
    }
    offerCard.dataset.joyConfigModule = 'offers';
    offerCard.classList.add('joy-preview-config-target');
    joyShow(offerCard, 'flex');
  } else {
    joyHide(offerCard);
  }

  if (growthId && campaignCard) {
    populateJoyGrowthCard(campaignCard, growthId);
    joyShow(campaignCard, 'flex');
  } else {
    joyHide(campaignCard);
  }

  if (hasOffer || growthId) {
    joyShow(promoRow, 'flex');
  } else {
    joyHide(promoRow);
  }
}

const JOY_BUILD_MASK_IDS = [
  'clientWifiFab',
  'clientLocationMapFab',
  'clientLocationHoursFab',
  'clientReservationFab',
  'clientPremiumBottomDock',
  'clientFabBar',
  'layoutBlockMenu',
  'locationMenuBtn',
  'layoutBlockReview',
  'feedbackBtn',
  'clientPremiumSectionDuo',
  'clientCustomNeedsTitle',
  'clientConnectPromoRow',
  'clientConnectOfferCard',
  'clientConnectCampaignCard'
];

function clearJoyBuildMask() {
  const page = document.getElementById('clientPage');
  page?.removeAttribute('data-joy-build');
  page?.classList.remove('joy-preview-empty');
  document.getElementById('joyCustomHeaderLogo')?.remove();

  JOY_BUILD_MASK_IDS.forEach((id) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.style.removeProperty('display');
    el.removeAttribute('aria-hidden');
    delete el.dataset.joyMask;
  });
  document
    .querySelectorAll('#clientTemplateSocialRow .client-template-header__social-btn')
    .forEach((el) => {
      el.style.removeProperty('display');
      el.removeAttribute('aria-hidden');
      delete el.dataset.joyMask;
    });
  document.querySelectorAll('[data-joy-mask]').forEach((el) => {
    delete el.dataset.joyMask;
  });
  document.querySelectorAll('#clientPage [data-joy-revealed]').forEach((el) => {
    delete el.dataset.joyRevealed;
    el.classList.remove('joy-appear');
  });
  const header = document.getElementById('clientTemplateHeader');
  if (header) {
    header.style.removeProperty('display');
    header.removeAttribute('aria-hidden');
  }
}

function syncJoyEmptyBrand(joyState, settings = {}) {
  const joyPage = document.getElementById('joyAssistantPage');
  const shell = joyPage?.querySelector('.joy-assistant-shell');
  if (!shell) return;

  let el = document.getElementById('joyEmptyStageBrand');
  const images = (joyState.images || []).filter(Boolean);
  const show = isJoyPreviewActive() && images.length === 0;

  if (!show) {
    el?.setAttribute('hidden', '');
    return;
  }
  if (!el) {
    el = document.createElement('div');
    el.id = 'joyEmptyStageBrand';
    el.className = 'joy-empty-stage-brand';
    el.setAttribute('aria-hidden', 'true');
    shell.prepend(el);
  }
  el.textContent = String(settings?.companyName || '').trim() || 'Locația ta';
  el.removeAttribute('hidden');
}

function markVisibleClientModules() {
  Object.entries(JOY_PREVIEW_CLICK_SELECTORS).forEach(([moduleId, selector]) => {
    selector.split(',').forEach((sel) => {
      document.querySelectorAll(`#clientPage ${sel.trim()}`).forEach((el) => {
        if (isElementVisible(el)) markJoyConfig(el, moduleId);
      });
    });
  });
}

function clearModuleSaveListener() {
  if (moduleSaveListener) {
    document.removeEventListener('sj:client-interface-module-saved', moduleSaveListener);
    moduleSaveListener = null;
  }
  pendingModuleId = null;
}

function openJoyModuleConfig(joyModuleId, onSaved) {
  const subsectionId = JOY_MODULE_SUBSECTION[joyModuleId];
  if (!subsectionId) {
    showSnackbar('Modulul nu are încă formular de configurare.');
    return;
  }

  clearModuleSaveListener();
  pendingModuleId = joyModuleId;

  moduleSaveListener = (e) => {
    const savedId = e?.detail?.moduleId;
    if (savedId !== subsectionId) return;
    clearModuleSaveListener();
    closeClientInterfaceModulePopup();
    onSaved?.(joyModuleId);
    showSnackbar('Modul salvat — preview actualizat.');
  };
  document.addEventListener('sj:client-interface-module-saved', moduleSaveListener);
  openClientInterfaceSubsection(subsectionId);
}

function resolveJoyModuleFromClick(target) {
  const marked = target.closest('[data-joy-config-module]');
  if (marked?.dataset?.joyConfigModule) return marked.dataset.joyConfigModule;

  for (const [moduleId, selector] of Object.entries(JOY_PREVIEW_CLICK_SELECTORS)) {
    if (target.closest(selector)) return moduleId;
  }
  return null;
}

function onJoyPreviewClick(e) {
  if (!isJoyPreviewActive()) return;

  const chromeModule = e.target.closest('.joy-preview-top-chrome [data-joy-config-module]');
  if (chromeModule?.dataset?.joyConfigModule) {
    e.preventDefault();
    e.stopPropagation();
    openJoyModuleConfig(chromeModule.dataset.joyConfigModule, () => {
      syncAfterSave?.();
    });
    return;
  }

  if (e.target.closest('#joyAssistantPage, .client-interface-module-popup, .popup.active')) return;

  const moduleId = resolveJoyModuleFromClick(e.target);
  if (!moduleId) return;

  e.preventDefault();
  e.stopPropagation();

  openJoyModuleConfig(moduleId, () => {
    syncAfterSave?.();
  });
}

let syncAfterSave = null;

export function setJoyPreviewSyncCallback(fn) {
  syncAfterSave = typeof fn === 'function' ? fn : null;
}

export function bindJoyPreviewClicks() {
  if (previewClickBound) return;
  previewClickBound = true;
  document.addEventListener('click', onJoyPreviewClick, true);
}

export function unbindJoyPreviewClicks() {
  if (!previewClickBound) return;
  document.removeEventListener('click', onJoyPreviewClick, true);
  previewClickBound = false;
  clearModuleSaveListener();
}

let lastJoyState = null;
let settingsRefreshedListener = null;

export async function syncRealClientPreview(joyState) {
  if (!joyState || !isJoyPreviewActive()) return;
  lastJoyState = joyState;

  const settings = getCurrentSettings();
  if (!settings) return;
  // „Refă pagina”: previzualizarea arată pagina nouă (golită + ce ai completat), nu pe cea veche
  overlayFreshInMemory(settings, joyState.ai);

  // Build mode: designul Custom e forțat de getActiveClientTemplateStyleForUi
  // cât timp body are .joy-assistant-client-preview (doar staging/localhost).
  patchSettingsForPreview(settings, joyState, { buildMode: true });

  try {
    const { applyClientUI } = await import('./slideshow.js?v=83.21');
    applyClientUI();
  } catch (e) {
    logger.warn('[JoyAssistant] applyClientUI failed:', e);
    return;
  }

  const previewSettings = buildJoyPreviewSettings(getCurrentSettings(), joyState);

  if (isCustomTemplateActive()) {
    // Pagina se construiește live pe designul Custom — header real, fără chrome suplimentar
    hideJoyPreviewTopChrome();
    const runMask = () => {
      if (!isJoyPreviewActive() || !isCustomTemplateActive()) return;
      clearJoyConfigMarks();
      applyJoyBuildMask(joyState, previewSettings);
      markVisibleClientModules();
      updateJoyLayoutHints();
    };
    runMask();
    syncJoyEmptyBrand(joyState, previewSettings);
    // syncCustomTemplate rulează async + rAF — reaplicăm masca după ce termină
    requestAnimationFrame(runMask);
    setTimeout(runMask, 160);
    setTimeout(runMask, 420);
    return;
  }

  await applyJoyBackground(joyState);

  applyJoyDraftExtras(joyState);
  syncJoyPreviewTopChrome(joyState, previewSettings);
  syncElegantTopChrome(previewSettings, { joyStep3Modules: joyState.step3Modules || [] });
  fixJoyClientBottomLayout();
  clearJoyConfigMarks();
  markVisibleClientModules();
  updateJoyLayoutHints();
  requestAnimationFrame(() => {
    fixJoyClientBottomLayout();
    updateJoyLayoutHints();
  });
}

export function openJoyModuleConfigurator(moduleId, onSaved) {
  openJoyModuleConfig(moduleId, onSaved);
}

export function activateRealClientPreview() {
  document.body.classList.add('joy-assistant-client-preview');
  bindJoyPreviewClicks();
  if (!settingsRefreshedListener) {
    // Listener-ul Firestore înlocuiește obiectul de setări (pierde patch-ul de
    // preview) și re-rulează sync-urile temei — reaplicăm patch + mască.
    settingsRefreshedListener = () => {
      if (!lastJoyState || !isJoyPreviewActive()) return;
      setTimeout(() => syncRealClientPreview(lastJoyState), 0);
    };
    document.addEventListener('sj:settings-refreshed', settingsRefreshedListener);
  }
}

export function deactivateRealClientPreview(options = {}) {
  document.body.classList.remove('joy-assistant-client-preview');
  if (settingsRefreshedListener) {
    document.removeEventListener('sj:settings-refreshed', settingsRefreshedListener);
    settingsRefreshedListener = null;
  }
  lastJoyState = null;
  delete window.__joyPreviewImages;
  hideJoyPreviewTopChrome();
  clearJoyBuildMask();
  document.getElementById('joyEmptyStageBrand')?.setAttribute('hidden', '');
  unbindJoyPreviewClicks();
  if (options.skipRestore) {
    settingsBackup = null;
    clearJoyConfigMarks();
  } else {
    restoreSettingsAfterJoyPreview();
  }
  import('./slideshow.js?v=83.21')
    .then((m) => m.stopSlideshow?.())
    .catch(() => {});
}
